import classNames from 'classnames';
import type React from 'react';
import { isValidElement, useRef } from 'react';
import type { TooltipProps } from '../Tooltip';
import { Tooltip } from '../Tooltip';
import { TextTooltip } from '../Typography';

export interface TooltipContentProps
  extends Pick<
    TooltipProps,
    | 'offset'
    | 'delay'
    | 'placement'
    | 'interactive'
    | 'followCursor'
    | 'trigger'
    | 'noArrow'
    | 'fixedWidth'
    | 'interactiveBorder'
    | 'appendTo'
    | 'anchorRef'
    | 'background'
    | 'visible'
  > {
  title: React.ReactNode;
  width?: number;
}

const TooltipContent = ({
  title,
  width,
  visible,
  noArrow = true,
  offset = [0, 2],
  delay = [700, 0],
  interactive = false,
  fixedWidth = true,
  background = 'dark',
  ...props
}: TooltipContentProps) => (
  <Tooltip
    shadow="lg"
    noArrow={noArrow}
    offset={offset}
    delay={delay}
    interactive={interactive}
    fixedWidth={fixedWidth}
    background={background}
    visible={visible}
    {...props}
  >
    <div
      className="px-1 pb-1 pt-0 leading-none break-words"
      aria-label={`${isValidElement(title) ? 'label' : title}-tooltip`}
      style={width ? { width: 'auto', maxWidth: width } : {}}
    >
      {isValidElement(title) ? (
        title
      ) : (
        <TextTooltip
          label={title}
          color="text-ds-natural-50"
        />
      )}
    </div>
  </Tooltip>
);

export interface TooltipBubbleProps
  extends Omit<TooltipContentProps, 'anchorRef'> {
  children?: React.ReactNode;
  absolute?: boolean;
  disabled?: boolean;
  anchorRef?: React.RefObject<HTMLElement>;
}

export const TooltipBubble = ({
  children,
  absolute,
  disabled,
  anchorRef,
  ...props
}: TooltipBubbleProps) => {
  const currentRef = useRef(null);

  if (anchorRef) {
    return (
      !disabled && (
        <TooltipContent
          anchorRef={anchorRef}
          {...props}
        />
      )
    );
  }

  return (
    <div className={classNames({ relative: absolute })}>
      <div ref={currentRef}>{children}</div>
      {!disabled && (
        <TooltipContent
          anchorRef={currentRef}
          {...props}
        />
      )}
    </div>
  );
};
